/**
 * VideoSelectionDialog — pick an existing recorded game OR create a new one
 * by pasting a video URL.
 *
 * Mirrors openVideoSelectionDialog + openNewVideoDialog from EstadisticasNew.js,
 * unified into a single dialog with two modes.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box,
  Button, Typography, TextField, List, ListItemButton, Tabs, Tab, MenuItem,
} from '@mui/material';
import { useStatsTheme, withAlpha } from '../../theme/statsTheme';

const NEW_GAME_DEFAULT = () => ({
  game_label: '',
  adversary_name: '',
  number_of_periods: 4,
  period_duration: 10,
  game_date: new Date().toISOString().split('T')[0],
  game_location: 'domicile',
  score: '',
  notes: '',
});

export default function VideoSelectionDialog({
  open,
  onClose,
  availableGames = [],
  onLoadMore,
  hasMoreGames,
  onSelectExisting,
  onCreateNew,
  t,
}) {
  const theme = useStatsTheme();
  const [tab, setTab] = useState(0);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newGame, setNewGame] = useState(NEW_GAME_DEFAULT);

  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  const updateNewGame = (k, v) => setNewGame((prev) => ({ ...prev, [k]: v }));

  const handleCreateNew = () => {
    if (!newGame.game_label.trim() || !newVideoUrl.trim()) return;
    onCreateNew({ ...newGame, isNewGame: true }, newVideoUrl.trim());
    setNewGame(NEW_GAME_DEFAULT());
    setNewVideoUrl('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.surfaces.elevated,
          color: theme.text.primary,
          border: `1px solid ${theme.accentBorder}`,
          borderRadius: theme.radius.xl,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
        {tt('videoSelection', 'Video stats')}
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 3,
          borderBottom: `1px solid ${theme.surfaces.divider}`,
          '& .MuiTab-root': { color: theme.text.secondary, textTransform: 'none' },
          '& .Mui-selected': { color: theme.accent },
          '& .MuiTabs-indicator': { backgroundColor: theme.accent },
        }}
      >
        <Tab label={tt('selectExisting', 'Select existing')} />
        <Tab label={tt('createNew', 'Create new')} />
      </Tabs>

      <DialogContent>
        {tab === 0 && (
          <Box sx={{ pt: 1 }}>
            {availableGames.length === 0 && (
              <Typography sx={{ color: theme.text.muted, fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                {tt('noGamesFound', 'No saved games for this group')}
              </Typography>
            )}
            <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {availableGames.map((g) => {
                const game = g.game || g;
                return (
                  <ListItemButton
                    key={game.id}
                    onClick={() => onSelectExisting(g)}
                    sx={{
                      borderRadius: theme.radius.md,
                      mb: 0.6,
                      backgroundColor: theme.surfaces.floating,
                      border: `1px solid ${theme.surfaces.border}`,
                      '&:hover': {
                        backgroundColor: withAlpha(theme.accent, 0.08),
                        borderColor: theme.accentBorder,
                      },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.text.primary }}>
                        {game.game_label} <Typography component="span" sx={{ color: theme.text.secondary, fontWeight: 400 }}>vs {game.adversary_name}</Typography>
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: theme.text.secondary }}>
                        {game.game_date} · {game.score || '—'} · {game.url ? '🎬 video' : 'no video'}
                      </Typography>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
            {hasMoreGames && (
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button onClick={onLoadMore} sx={{ color: theme.accent }}>
                  {tt('loadMore', 'Load more')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'grid', gap: 1.5, mt: 1 }}>
            <TextField
              label={tt('videoUrl', 'Video URL (YouTube, Vimeo…)')}
              value={newVideoUrl}
              onChange={(e) => setNewVideoUrl(e.target.value)}
              size="small"
              fullWidth
              autoFocus
            />
            <TextField
              label={tt('gameLabel', 'Game label')}
              value={newGame.game_label}
              onChange={(e) => updateNewGame('game_label', e.target.value)}
              size="small"
            />
            <TextField
              label={tt('adversary', 'Adversary')}
              value={newGame.adversary_name}
              onChange={(e) => updateNewGame('adversary_name', e.target.value)}
              size="small"
            />
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                label={tt('periods', 'Periods')}
                type="number"
                value={newGame.number_of_periods}
                onChange={(e) => updateNewGame('number_of_periods', Number(e.target.value) || 4)}
                size="small"
              />
              <TextField
                label={tt('periodDuration', 'Period duration (min)')}
                type="number"
                value={newGame.period_duration}
                onChange={(e) => updateNewGame('period_duration', Number(e.target.value) || 10)}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr 1fr' }}>
              <TextField
                label={tt('date', 'Date')}
                type="date"
                value={newGame.game_date}
                onChange={(e) => updateNewGame('game_date', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label={tt('location', 'Location')}
                value={newGame.game_location}
                onChange={(e) => updateNewGame('game_location', e.target.value)}
                size="small"
              >
                <MenuItem value="domicile">{tt('home', 'Home')}</MenuItem>
                <MenuItem value="exterieur">{tt('away', 'Away')}</MenuItem>
              </TextField>
              <TextField
                label={tt('finalScore', 'Final score')}
                value={newGame.score}
                onChange={(e) => updateNewGame('score', e.target.value)}
                placeholder="78-62"
                size="small"
              />
            </Box>
            <TextField
              label={tt('notes', 'Notes')}
              value={newGame.notes}
              onChange={(e) => updateNewGame('notes', e.target.value)}
              multiline
              rows={2}
              size="small"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: theme.text.secondary }}>
          {tt('cancel', 'Cancel')}
        </Button>
        {tab === 1 && (
          <Button
            onClick={handleCreateNew}
            variant="contained"
            disabled={!newGame.game_label.trim() || !newVideoUrl.trim()}
            sx={{
              backgroundColor: theme.accent,
              '&:hover': { backgroundColor: theme.accentStrong },
            }}
          >
            {tt('start', 'Start')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
